import "@fontsource-variable/geist/wght.css";
import "@fontsource-variable/geist-mono/wght.css";
import "@otrack/ui/styles.css";
import "@otrack/app-shell/styles.css";
import "@otrack/client/styles.css";
import { StrictMode, useCallback, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { ClerkFailed, ClerkLoaded, ClerkLoading, ClerkProvider, useAuth, useUser } from "@clerk/react";
import { OTrackApp, type AppStore } from "@otrack/app-shell";
import { accountDatabase, AuthProblem, LocalApp, SHARED_DATABASE, Splash, useSyncServices } from "@otrack/client";
import type { LocalStore } from "@otrack/local-store";
import { WebLocalStore } from "@otrack/local-store-web";
import { SsoCallbackScreen, SSO_CALLBACK_PATH, WebSignInScreen } from "./sign-in";

const clerkKey = import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"] as string | undefined;

/** The signed-in app: this account's own data on this device, synced to its Vault. */
function AccountApp({ userId }: { userId: string }) {
  const database = useCallback(() => accountDatabase(userId, { exists: (name) => WebLocalStore.exists(name), storage: localStorage }), [userId]);
  return <LocalApp database={database}>{(store, local) => <SyncedApp store={store} local={local}/>}</LocalApp>;
}

/** Google's consent comes back here once connected; older desktop apps open `connect` to start it. */
const GOOGLE_RETURN = "google-calendar";
const CONNECT_REQUEST = "connect";
const googleConsent = { returnTo: "web", open: (url: string) => window.location.assign(url) } as const;

function SyncedApp({ store, local }: { store: AppStore; local: LocalStore }) {
  const { getToken, signOut } = useAuth();
  const { user } = useUser();
  const tokenProvider = useCallback(() => getToken(), [getToken]);
  const label = user?.primaryEmailAddress?.emailAddress ?? null;

  const account = useMemo(() => ({ label, getToken: tokenProvider, signOut: () => void signOut(), googleConsent }), [label, tokenProvider, signOut]);
  const services = useSyncServices(store, local, account);

  // Back from Google, or sent by an older desktop app to connect: tidy the address, then carry on.
  // Google Calendar checks the connection when it starts, so coming back needs nothing more.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(GOOGLE_RETURN) && params.get(CONNECT_REQUEST) !== GOOGLE_RETURN) return;
    window.history.replaceState(null, "", `/${window.location.hash || "#/settings"}`);
    if (!params.has(GOOGLE_RETURN)) void Promise.resolve(services.google?.connect()).catch((cause: unknown) => console.error("Connecting Google Calendar failed", cause));
    // Only on arrival.
  }, []);

  return <OTrackApp store={store} services={services}/>;
}

/** Nothing of ontrack, not even this device's data, shows until Clerk confirms who is signed in. */
function SignInGate() {
  const { isSignedIn, userId } = useAuth();
  const returning = window.location.pathname === SSO_CALLBACK_PATH;
  if (!isSignedIn || !userId) return returning ? <SsoCallbackScreen/> : <WebSignInScreen/>;
  if (returning) window.history.replaceState(null, "", "/");
  return <AccountApp key={userId} userId={userId}/>;
}

const devAccount = { label: "Local development user", getToken: async () => null };
const devDatabase = async () => SHARED_DATABASE;

/** Local development without Clerk: the dev Worker treats every request as one local user. */
function DevApp({ store, local }: { store: AppStore; local: LocalStore }) {
  const services = useSyncServices(store, local, devAccount);
  return <OTrackApp store={store} services={services}/>;
}

function Root() {
  if (clerkKey) {
    return <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <ClerkFailed><AuthProblem title="Can't reach sign-in">ontrack checks who you are before it opens. Check your connection and try again.</AuthProblem></ClerkFailed>
      <ClerkLoading><Splash/></ClerkLoading>
      <ClerkLoaded><SignInGate/></ClerkLoaded>
    </ClerkProvider>;
  }
  if (import.meta.env.DEV) return <LocalApp database={devDatabase}>{(store, local) => <DevApp store={store} local={local}/>}</LocalApp>;
  // A production build without Clerk would be open to anyone with the address.
  return <AuthProblem title="Sign-in isn't set up" retry={false}>This build has no Clerk key, so ontrack stays locked. Set VITE_CLERK_PUBLISHABLE_KEY and deploy again.</AuthProblem>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><Root/></StrictMode>);
